<script lang="ts">
type TableVirtual =
	| {
			enabled: false;
	  }
	| {
			enabled: true;
			height: number;
			itemSize: number;
	  };
</script>
<script setup lang="ts" generic="TData">
import type {
	ColumnDef,
	ColumnDefResolved,
	ColumnFiltersState,
	PaginationState,
	Row,
	RowSelectionState,
	SortingState,
} from "@tanstack/vue-table";
import type { VNode } from "vue";
import { computed, h } from "vue";
import Column from "./Column.vue";
import ColumnSelection from "./ColumnSelection.vue";
import DataTableContent from "./DataTableContent.vue";
import DataTablePagination from "./DataTablePagination.vue";
import DataTableRoot, { type DataTableOptions } from "./DataTableRoot.vue";
import DataTableVirtualizer from "./DataTableVirtualizer.vue";

const props = withDefaults(
	defineProps<{
		columns?: ColumnDef<TData, any>[];
		data?: TData[];
		options?: DataTableOptions<TData>;
		showSelection?: boolean;
		singleSelect?: boolean;
		defaultSort?: SortingState;
		defaultPageSize?: number;
		disablePagination?: boolean;
		disableHeader?: boolean;
		rowActive?: (row: Row<TData>) => boolean;
		rowDisabled?: (row: Row<TData>) => boolean;
		virtual?: TableVirtual;

		hidePageCount?: boolean;
		loading?: boolean;
		noAllRows?: boolean;
	}>(),
	{
		columns: () => [],
		data: () => [],
	}
);

const emit = defineEmits<{
	"click:row": [row: Row<TData>];
}>();

const globalFilter = defineModel<string>("globalFilter", { default: "" });
const columnFilters = defineModel<ColumnFiltersState>("columnFilters", { default: () => [] });
const selections = defineModel<RowSelectionState>("selections", { default: () => ({}) });
const pagination = defineModel<PaginationState & { pageCount?: number }>("pagination", {
	default: (p) => ({ pageIndex: 0, pageSize: p.defaultPageSize || 20 }),
});

const sorting = defineModel<SortingState>("sorting", { default: (props) => props.defaultSort });

// eslint-disable-next-line @typescript-eslint/no-unsafe-function-type
function parseSlot(slot: string | Function | null | undefined) {
	if (typeof slot === "function") {
		return slot;
	}

	if (slot != null) {
		return () => slot;
	}

	return undefined;
}

function Content(contentProps: any = {}) {
	const columns: VNode[] = [];

	if (props.showSelection) {
		columns.push(h(ColumnSelection));
	}

	for (const column of props.columns) {
		const resolved = column as ColumnDefResolved<TData>;

		columns.push(
			h(
				Column,
				{
					id: column.id,
					field: resolved.accessorKey,
					fieldFn: resolved.accessorFn as any,

					// All default boolean from tanstack-core
					enableGlobalFilter: true,
					enableSorting: true,
					enableColumnFilter: true,
					enableGrouping: true,
					enableHiding: true,
					enableMultiSort: false,
					enablePinning: true,
					enableResizing: true,
					...(resolved as any),
				},
				{
					header: parseSlot(resolved.header),
					default: parseSlot(resolved.cell),
				}
			)
		);
	}

	return h(
		DataTableContent,
		{ "onClick:row": (row) => emit("click:row", row.row as any), ...contentProps },
		() => columns
	);
}

const dataTableOptions = computed<DataTableOptions<TData>>(() => {
	const options: DataTableOptions<TData> = { ...props.options } as any;

	if (pagination.value.pageCount != null) {
		options.pageCount = pagination.value.pageCount;
	}

	options.enableMultiRowSelection = !props.singleSelect;

	return options;
});
</script>

<template>
	<DataTableRoot
		v-model:pagination="pagination"
		v-model:selection="selections"
		v-model:filter="globalFilter"
		v-model:fieldFilters="columnFilters"
		v-model:sorting="sorting"
		:options="dataTableOptions"
		class="flex flex-col gap-2"
		:data="props.data"
		:loading="props.loading"
	>
		<DataTableVirtualizer
			v-if="props.virtual?.enabled"
			v-slot="{ height }"
			:estimateSize="props.virtual.itemSize"
			:height="props.virtual.height"
		>
			<Content
				:style="{
					height: `${height}px`,
				}"
			/>
		</DataTableVirtualizer>

		<Content v-else />

		<div v-if="!props.disablePagination" class="flex w-full justify-end px-2 py-2">
			<DataTablePagination :infinite="props.options?.manualPagination" />
		</div>
	</DataTableRoot>
</template>
